<script setup>
import { formatTelLink } from '@/composables/useFormatTelLink';
import { ref } from 'vue';
import { useMediaQuery } from '@vueuse/core';

import UkravtoGroup from '@/components/layout/Header/UkravtoGroup.vue';
import DealersAndDistributorsTabs from '@/components/DealersAndDistributorsTabs.vue';

import ApplicationsList from '@/components/ApplicationsList.vue';
import SocialsList from '@/components/SocialsList.vue';
defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  nav: {
    type: Array,
    default: () => [],
  },
  link: {
    type: Object,
    default: () => {},
  },
  contacts: {
    type: Object,
    default: () => {},
  },
  socials: {
    type: Array,
    default: () => [],
  },
  ukravtoGroup: {
    type: Object,
    default: () => {},
  },
});

const isTablet = useMediaQuery('(min-width: 768px)');

const emit = defineEmits(['close', 'navigate']);

const currentSubmenu = ref(null);
const currentCarCategory = ref(null);
const activeBrandTab = ref('dealers');

const hasChildren = (item) => {
  return (
    (item.children && item.children.length > 0) ||
    (item.type === 'cars' && (item.cars || item.brands))
  );
};

const handleMenuClick = (item) => {
  if (item.type === 'cars') {
    currentSubmenu.value = item;
    activeBrandTab.value = 'dealers';
  } else if (hasChildren(item)) {
    currentSubmenu.value = item;
  } else {
    emit('navigate', item.url);
    closeMenu();
  }
};

const handleLinkClick = (link) => {
  emit('navigate', link.url);
  closeMenu();
};

const handleSubmenuClick = (item) => {
  if (item.children && item.children.length > 0) {
    // TODO: Handle nested submenu if needed
  } else {
    emit('navigate', item.url);
    closeMenu();
  }
};

const closeCarCategory = () => {
  currentCarCategory.value = null;
};

const openCompanySubmenu = () => {
  currentSubmenu.value = { type: 'company' };
};

const goBack = () => {
  if (currentCarCategory.value) {
    closeCarCategory();
  } else {
    currentSubmenu.value = null;
  }
};

const closeMenu = () => {
  currentSubmenu.value = null;
  currentCarCategory.value = null;
  activeBrandTab.value = 'dealers';
  emit('close');
};
</script>
<template>
  <div class="mobile-menu">
    <div v-if="isOpen" class="mobile-menu__overlay" @click="closeMenu"></div>
    <div
      class="mobile-menu__container"
      :class="{ 'mobile-menu__container--open': isOpen }"
    >
      <div
        class="mobile-menu__panel mobile-menu__panel--main"
        :class="{ 'mobile-menu__panel--hidden': currentSubmenu }"
      >
        <div class="mobile-menu__header">
          <span class="mb-0 mobile-menu__title h6">{{
            $t('header.menu')
          }}</span>
          <button
            type="button"
            class="mobile-menu__close js-button-mobile-close"
            :aria-label="$t('header.close_menu')"
            @click="closeMenu"
          >
            <CIcon
              name="close-white"
              width="16"
              height="16"
              class="mobile-menu__close-icon"
            />
          </button>
        </div>

        <div class="mobile-menu__content">
          <ul v-if="nav" class="m-0 p-0 mobile-menu__items">
            <template v-for="item in nav" :key="item.title">
              <li v-if="item.url" class="m-0 mobile-menu__item">
                <div class="mobile-menu__item-content">
                  <CTagDetect
                    :to="item.url"
                    class="mobile-menu__item-text text-uppercase h5"
                    >{{ item.title }}
                  </CTagDetect>
                  <CIcon
                    v-if="hasChildren(item)"
                    name="direction-small-white"
                    width="16"
                    height="16"
                    class="mobile-menu__item-arrow"
                    @click="handleMenuClick(item)"
                  />
                </div>
              </li>
              <li
                v-else
                class="m-0 mobile-menu__item"
                @click="handleMenuClick(item)"
              >
                <div class="mobile-menu__item-content">
                  <span
                    class="mobile-menu__item-text text-uppercase h5"
                    v-html="item.title"
                  ></span>
                  <CIcon
                    v-if="hasChildren(item)"
                    name="direction-small-white"
                    width="16"
                    height="16"
                    class="mobile-menu__item-arrow"
                  />
                </div>
              </li>
            </template>
          </ul>
          <div v-if="link && !isTablet" class="mobile-menu__corporate">
            <div class="mobile-menu__item" @click="handleLinkClick(link)">
              <div class="mobile-menu__item-content">
                <span
                  class="mobile-menu__item-text h5"
                  v-html="link.title"
                ></span>
              </div>
            </div>
          </div>
          <div v-if="contacts" class="mobile-menu__contacts">
            <div class="mobile-menu__contacts-wrap">
              <div class="mobile-menu__contacts-info">
                <div class="mobile-menu__contacts-phone">
                  <CIcon
                    class="mobile-menu__contacts-phone-icon"
                    name="phone-red"
                    width="24"
                    height="24"
                  />
                  <template v-for="phone in contacts?.phone" :key="phone">
                    <a
                      :href="`tel:${formatTelLink(phone)}`"
                      class="body-1-bold"
                      target="_blank"
                    >
                      {{ phone }}</a
                    >
                  </template>
                </div>
                <SocialsList
                  v-if="contacts?.messengers && contacts?.messengers.length > 0"
                  :data="contacts?.messengers"
                  :view="'light'"
                  class="mobile-menu__contacts-messengers"
                />
              </div>
              <ApplicationsList
                v-if="
                  contacts?.applications && contacts?.applications.length > 0
                "
                :data="contacts?.applications"
                :view="'light'"
                class="mobile-menu__contacts-applications"
                :buttons-prefix-class="'js-button-mobile-application'"
              />
            </div>
            <SocialsList
              v-if="socials && socials.length > 0"
              :data="socials"
              :view="'light'"
              class="mobile-menu__contacts-socials"
            />
          </div>
          <div v-if="ukravtoGroup" class="mobile-menu__company">
            <div class="mobile-menu__company-item" @click="openCompanySubmenu">
              <span
                class="mb-0 mobile-menu__company-item-title white h5"
                v-html="ukravtoGroup.title"
              ></span>
              <button
                type="button"
                class="mobile-menu__company-arrow js-button-mobile-company"
                :aria-label="
                  $t('header.toggle_company_submenu', {
                    title: ukravtoGroup.title,
                  })
                "
              >
                <CIcon name="direction-small-white" width="16" height="16" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="
          currentSubmenu &&
          currentSubmenu.type !== 'cars' &&
          currentSubmenu.type !== 'company'
        "
        class="mobile-menu__panel mobile-menu__panel--submenu"
        :class="{ 'mobile-menu__panel--visible': currentSubmenu }"
      >
        <div class="mobile-menu__header">
          <button
            type="button"
            class="mobile-menu__back js-button-mobile-back"
            @click="goBack"
          >
            <CIcon
              name="direction-white"
              width="16"
              height="16"
              class="mobile-menu__back-icon"
            />
          </button>
          <p
            class="mb-0 mobile-menu__title text-center text-uppercase h6"
            v-html="currentSubmenu.title"
          ></p>
          <button
            type="button"
            class="mobile-menu__close js-button-mobile-close"
            :aria-label="$t('header.close_menu')"
            @click="closeMenu"
          >
            <CIcon
              name="close-white"
              width="16"
              height="16"
              class="mobile-menu__close-icon"
            />
          </button>
        </div>
        <div class="mobile-menu__content">
          <ul v-if="currentSubmenu.children" class="m-0 p-0 mobile-menu__items">
            <li
              v-for="item in currentSubmenu.children"
              :key="item.title"
              class="m-0 p-0 mobile-menu__item"
              @click="handleSubmenuClick(item)"
            >
              <div class="mobile-menu__item-content">
                <span
                  class="mobile-menu__item-text h5"
                  v-html="item.title"
                ></span>
                <CIcon
                  v-if="item.children"
                  name="direction-small-white"
                  width="16"
                  height="16"
                  class="mobile-menu__item-arrow"
                />
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div
        v-if="currentSubmenu && currentSubmenu.type === 'cars'"
        class="mobile-menu__panel mobile-menu__panel--submenu"
        :class="{
          'mobile-menu__panel--visible': currentSubmenu.type === 'cars',
        }"
      >
        <div class="mobile-menu__header">
          <button
            type="button"
            class="mobile-menu__back js-button-mobile-back"
            @click="goBack"
          >
            <CIcon
              name="direction-white"
              width="16"
              height="16"
              class="mobile-menu__back-icon"
            />
          </button>
          <p
            class="mb-0 mobile-menu__title text-center text-uppercase h6"
            v-html="currentSubmenu.title"
          ></p>
          <button
            type="button"
            class="mobile-menu__close js-button-mobile-close"
            :aria-label="$t('header.close_menu')"
            @click="closeMenu"
          >
            <CIcon
              name="close-white"
              width="16"
              height="16"
              class="mobile-menu__close-icon"
            />
          </button>
        </div>
        <div class="mobile-menu__content">
          <ul v-if="currentSubmenu.cars" class="m-0 p-0 mobile-menu__items">
            <li
              v-for="carCategory in currentSubmenu.cars"
              :key="carCategory.id"
              class="m-0 mobile-menu__item"
              @click="handleLinkClick(carCategory)"
            >
              <div class="mobile-menu__item-content">
                <span
                  class="mobile-menu__item-text h5"
                  v-html="carCategory.title"
                ></span>
              </div>
            </li>
          </ul>
          <div v-if="currentSubmenu.brands" class="mobile-menu__brands">
            <DealersAndDistributorsTabs
              :data="currentSubmenu.brands"
              :view="'nav-mobile'"
            />
          </div>
        </div>
      </div>
      <div
        v-if="currentSubmenu && currentSubmenu.type === 'company'"
        class="mobile-menu__panel mobile-menu__panel--submenu"
        :class="{
          'mobile-menu__panel--visible': currentSubmenu.type === 'company',
        }"
      >
        <div class="mobile-menu__header">
          <button
            type="button"
            class="mobile-menu__back js-button-mobile-back"
            @click="goBack"
          >
            <CIcon
              name="direction-white"
              width="16"
              height="16"
              class="mobile-menu__back-icon"
            />
          </button>
          <p
            class="mb-0 mobile-menu__title text-center text-uppercase h6"
            v-html="ukravtoGroup?.title"
          ></p>
          <button
            type="button"
            class="mobile-menu__close js-button-mobile-close"
            :aria-label="$t('header.close_menu')"
            @click="closeMenu"
          >
            <CIcon
              name="close-white"
              width="16"
              height="16"
              class="mobile-menu__close-icon"
            />
          </button>
        </div>

        <div class="mobile-menu__content">
          <UkravtoGroup :ukravto-group="ukravtoGroup" />
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.mobile-menu {
  $parent: &;
  &__overlay {
    background-color: rgba($black, 0.7);
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 1000;
  }

  &__container {
    background-color: $black;
    bottom: 0;
    color: $white;
    max-width: 704px;
    left: 0;
    position: fixed;
    overflow: hidden;
    top: 0;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    width: 100%;
    z-index: 1001;

    &--open {
      transform: translateX(0);
    }
  }

  &__panel {
    background-color: $black;
    bottom: 0;
    display: flex;
    flex-direction: column;
    left: 0;
    padding: 24px;
    position: absolute;
    right: 0;
    top: 0;
    transform: translateX(0);
    transition: transform 0.3s ease;
    @include media-breakpoint-up(sm) {
      padding: 32px;
    }
    &--submenu {
      transform: translateX(100%);
      #{$parent} {
        &__header {
          @include media-breakpoint-up(sm) {
            padding: 0px 0 31px;
          }
        }
      }
    }

    &--visible {
      transform: translateX(0);
    }

    &--hidden {
      transform: translateX(-100%);
    }
  }

  &__header {
    align-items: center;
    border-bottom: 1px solid $on-surface-variant-2;
    display: flex;
    justify-content: space-between;
    padding-bottom: 24px;
    @include media-breakpoint-up(sm) {
      padding: 8px 0 48px;
    }
  }

  &__title {
    margin: 0;
    flex: 1;
    @include media-breakpoint-up(sm) {
      font-size: 20px;
    }
  }

  &__close,
  &__back {
    align-items: center;
    background: none;
    border: none;
    color: $white;
    cursor: pointer;
    display: flex;
    justify-content: center;
    height: 32px;
    padding: 0;
    width: 32px;
  }

  &__back {
    margin-left: -8px;
    @include media-breakpoint-up(sm) {
      margin-left: 0;
      border-radius: 50%;
      border: 1px solid $on-surface-variant-2;
      height: 48px;
      width: 48px;
    }
    &-icon {
      transform: rotate(180deg);
    }
  }
  &__close {
    margin-right: -8px;
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    padding: 24px 0 60px 0;
    @include media-breakpoint-up(sm) {
      padding: 29px 0 0px 0;
    }
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: 8px;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__item {
    cursor: pointer;
    padding: 0;

    &-content {
      display: flex;
      align-items: center;
      padding: 9px 0 10px;
      gap: 8px;
    }

    &-icon {
      height: 16px;
      width: 16px;
      object-fit: contain;
    }

    &-text {
      flex: 1;
      color: $white;
    }
  }

  &__corporate {
    margin-top: 8px;
  }

  &__brands {
    margin-top: 56px;
    @include media-breakpoint-up(sm) {
      margin-top: 77px;
    }
  }

  &__contacts {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-top: 22px;
    @include media-breakpoint-up(sm) {
      gap: 40px;
      margin-top: 36px;
    }
    &-wrap {
      align-items: start;
      background-color: $surface-container;
      border-radius: 4px;
      display: flex;
      flex-direction: column;
      gap: 32px;
      padding: 24px 16px 16px;
      @include media-breakpoint-up(sm) {
        column-gap: 32px;
        padding: 32px 32px 38px;
        row-gap: 30px;
      }
    }
    &-info {
      display: grid;
      grid-template-columns: 1fr;
      gap: 24px;
      width: 100%;
      @include media-breakpoint-up(sm) {
        grid-template-columns: 5.7fr 6.3fr;
      }
    }
    &-phone {
      align-items: center;
      display: grid;
      gap: 16px;
      grid-template-columns: 24px auto;
      max-width: fit-content;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 16);
        grid-template-columns: px-to-vw(24) auto;
      }
      &-icon {
        height: 100%;
        width: 100%;
      }
    }
    &-applications {
      margin: 7px 0;
      width: 100%;
      @include media-breakpoint-up(sm) {
        border-top: 1px solid $on-surface-variant-2;
        margin: 0;
        display: block;
        column-count: 2;
        column-gap: 8px;
        padding-top: 40px;
        &:deep(.applications-item) {
          margin-bottom: 6px;
        }
      }
    }
  }

  // Company section
  &__company {
    border-top: 1px solid $on-surface-variant-2;
    margin-top: 40px;
    padding-top: 16px;
  }

  &__company-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    padding: 10px 0;
  }

  &__company-arrow {
    cursor: pointer;
  }
}
</style>
